import { Component } from '@angular/core';
import { DESKTOP_THEMES, DEFAULT_DESKTOP_THEME, GlobalsService, HttpService, IDesktopTheme, UserSettingsService, UtilsService } from '@services';
import { ThemeService } from '@components/theme.service';

/**
 * Floating theme picker, shown on every page for company 3 only, so themes can be checked
 * page by page without a round trip to Preferences. A pick is saved to the user's settings the
 * same way the Preferences tab saves it, so it sticks across reloads.
 */
@Component({
	selector: 'theme-switcher',
	templateUrl: 'theme-switcher.component.html',
	styleUrl: 'theme-switcher.component.css',
	providers: [ThemeService],
	standalone: false,
})
export class ThemeSwitcherComponent {
	private static readonly darkOnlyKey = 'THEME_SWITCHER_DARK_ONLY';
	private static readonly expandedKey = 'THEME_SWITCHER_EXPANDED';

	theme: string = GlobalsService.userSettings?.theme || localStorage.getItem('theme') || DEFAULT_DESKTOP_THEME;
	darkOnly: boolean = localStorage.getItem(ThemeSwitcherComponent.darkOnlyKey) === 'true';
	// Starts minimized; stays open only once someone has opened it.
	isExpanded: boolean = localStorage.getItem(ThemeSwitcherComponent.expandedKey) === 'true';
	isSaving: boolean = false;

	constructor(private themeService: ThemeService, private httpService: HttpService) {}

	get themes(): IDesktopTheme[] {
		const themes = this.darkOnly ? DESKTOP_THEMES.filter(x => x.dark) : DESKTOP_THEMES;

		// Keep the current theme in the list even when it's filtered out, so the select doesn't go blank.
		if (!themes.some(x => x.id === this.theme)) {
			const current = DESKTOP_THEMES.find(x => x.id === this.theme);
			if (current) return [current, ...themes];
		}

		return themes;
	}

	toggleExpanded() {
		this.isExpanded = !this.isExpanded;
		localStorage.setItem(ThemeSwitcherComponent.expandedKey, this.isExpanded.toString());
	}

	onDarkOnlyChanged() {
		localStorage.setItem(ThemeSwitcherComponent.darkOnlyKey, this.darkOnly.toString());
	}

	// Steps through the (filtered) list, wrapping at either end.
	async step(direction: number) {
		const themes = this.darkOnly ? DESKTOP_THEMES.filter(x => x.dark) : DESKTOP_THEMES;
		const idx = themes.findIndex(x => x.id === this.theme);
		const next = idx < 0 ? (direction > 0 ? 0 : themes.length - 1) : (idx + direction + themes.length) % themes.length;

		await this.setTheme(themes[next].id);
	}

	async setTheme(themeId: string) {
		if (!themeId || themeId === this.theme) return;

		this.theme = themeId;
		localStorage.setItem('theme', themeId);
		await this.themeService.loadTheme(themeId);

		const userSettings = UtilsService.clone(GlobalsService.userSettings);
		if (!userSettings) return;

		userSettings.theme = themeId;

		this.isSaving = true;
		try {
			// Guarded because the http interceptor swallows errors and resolves undefined.
			const saved = await this.httpService.post('/userSettings/updateUserSettings', userSettings);
			if (saved) UserSettingsService.cacheUserSettings(saved);
		} finally {
			this.isSaving = false;
		}
	}
}
